import React from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/theme-github_dark";
import { mdiCodeJson } from "@mdi/js";
import { useTheme } from "../../util/ThemeProvider";
import { Button, ButtonVariant, Typography } from "../../atoms";
import { TypographyContent } from "../../atoms";
import { PanelColor, ThemeColor } from "../../props";
import { Panel } from "../../layouts/Panel";
import { getBackgroundStyles, incPanelColorLevel } from "../../styles";

export interface JSONEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  height?: string;
  name?: string;
  title?: TypographyContent;
  noHeader?: boolean;
  color?: PanelColor;
}

function tryFormat(value: string): string | null {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return null;
  }
}

export const JSONEditor: React.FC<JSONEditorProps> = ({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  height = "300px",
  name,
  title = { local: "Title" },
  noHeader = false,
  color = PanelColor.level2,
}) => {
  const { isGlobalDarkMode } = useTheme();

  const handleFormat = () => {
    const formatted = tryFormat(value);
    if (formatted !== null) onChange(formatted);
  };

  return (
    <Panel color={color} className="p-0 overflow-hidden">
      {!noHeader && (
        <div
          className={`flex items-center justify-between px-3 py-1.5 border-b border-inherit ${getBackgroundStyles(incPanelColorLevel(color, 1))}`}
        >
          <Typography.Span className="text-xs font-medium" content={title} />
          <Button
            variant={ButtonVariant.text}
            color={ThemeColor.gray}
            iconPath={mdiCodeJson}
            dense
            muted
            disabled={disabled || readOnly}
            onClick={handleFormat}
          />
        </div>
      )}
      <AceEditor
        mode="json"
        theme={isGlobalDarkMode ? "github_dark" : "github"}
        name={name}
        value={value}
        onChange={onChange}
        readOnly={disabled || readOnly}
        editorProps={{ $blockScrolling: true }}
        style={{ width: "100%", height }}
        setOptions={{ useWorker: false }}
      />
    </Panel>
  );
};
